/**
 * Session navigation journey — distinct page paths this tab has visited.
 * Used to gate a mini LLM pill-rank after serious browse (default 2 pages).
 * Pure presentation; no product hardcoding.
 */

const STORAGE_KEY = "bhai_path_journey_v1";

/** Distinct pages before we spend a mini LLM call on journey-aware pills. */
export const PATH_JOURNEY_LLM_THRESHOLD = 2;

/** Max paths retained (oldest drop). */
const MAX_PATHS = 12;

export type PathJourney = {
  /** Distinct pathnames in visit order (first seen). */
  paths: string[];
  /** Full land sequence including revisits (capped). */
  landings: string[];
  at: number;
};

export function normalizeJourneyPath(pathname?: string | null): string {
  const raw =
    pathname ??
    (typeof window !== "undefined" ? window.location.pathname : "/") ??
    "/";
  let p = String(raw || "/")
    .replace(/\/+/g, "/")
    .trim();
  if (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);
  return p || "/";
}

export function readPathJourney(): PathJourney | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw) as PathJourney;
    if (!o || !Array.isArray(o.paths)) return null;
    return {
      paths: o.paths.filter((x) => typeof x === "string").slice(0, MAX_PATHS),
      landings: Array.isArray(o.landings)
        ? o.landings.filter((x) => typeof x === "string").slice(-MAX_PATHS)
        : [],
      at: typeof o.at === "number" ? o.at : Date.now(),
    };
  } catch {
    return null;
  }
}

export function clearPathJourney(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Record a page land. Returns updated journey.
 * Call on SPA/page-voice load (once per URL change).
 */
export function recordPathLand(pathname?: string | null): PathJourney {
  const path = normalizeJourneyPath(pathname);
  const prev = readPathJourney();
  const paths = prev?.paths ? [...prev.paths] : [];
  if (!paths.includes(path)) paths.push(path);
  while (paths.length > MAX_PATHS) paths.shift();
  const landings = [...(prev?.landings ?? []), path].slice(-MAX_PATHS);
  const next: PathJourney = { paths, landings, at: Date.now() };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
  return next;
}

/** True when visitor has seen enough distinct pages to warrant journey LLM rank. */
export function isPathJourneySerious(
  journey?: PathJourney | null,
  threshold = PATH_JOURNEY_LLM_THRESHOLD,
): boolean {
  const j = journey ?? readPathJourney();
  return (j?.paths.length ?? 0) >= threshold;
}

/** Path stack for rank API (oldest → newest distinct). */
export function pathStackForRank(journey?: PathJourney | null): string[] {
  const j = journey ?? readPathJourney();
  return j?.paths?.slice(-MAX_PATHS) ?? [];
}
